import { useState, useMemo, useRef, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Search,
  Plus,
  Filter,
  X,
  LayoutGrid,
  List,
  ChevronLeft,
  ChevronRight,
  Send,
  UploadCloud,
} from "lucide-react";
import {
  api,
  apiError,
  type Paginated,
  type Ticket as TicketType,
  type Status,
  type Priority,
  type Category,
  type UserSummary,
} from "../lib/api";
import { GlassCard } from "../components/ui/GlassCard";
import { PriorityBadge } from "../components/tickets/PriorityBadge";
import { StatusBadge } from "../components/tickets/StatusBadge";
import { CategoryBadge } from "../components/tickets/CategoryBadge";
import { SLACountdown } from "../components/tickets/SLACountdown";
import { Avatar } from "../components/ui/Avatar";
import { EmptyState } from "../components/ui/EmptyState";
import { Modal } from "../components/ui/Modal";
import { Spinner } from "../components/ui/FullScreenLoader";
import { fmtRelative, truncate } from "../lib/format";
import { PRIORITIES, STATUSES, CATEGORIES, STATUS_META, PRIORITY_META, CATEGORY_META, PRIORITY_TEMPLATES, ISSUE_CATEGORIES } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { useItDepartments } from "../lib/departments";
import { cn } from "../lib/cn";

interface PillOption {
  value: string;
  label: string;
}

function MultiPill({
  label,
  options,
  value = [],
  onChange,
}: {
  label: string;
  options: PillOption[];
  value?: string[];
  onChange: (v: string[]) => void;
}) {
  const toggle = (v: string) =>
    value.includes(v)
      ? onChange(value.filter((x) => x !== v))
      : onChange([...value, v]);
  return (
    <div className="flex items-center gap-1 flex-wrap">
      <span className="text-[10px] uppercase tracking-wider text-slate-400 mr-1">
        {label}
      </span>
      {options.map((o) => {
        const active = value.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => toggle(o.value)}
            className={cn(
              "px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all",
              active
                ? "bg-brand-500/25 border-brand-400/50 text-white"
                : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10",
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// Module-scoped debounce timer for the search box (was window.__ticketsSearchT).
let searchDebounce: ReturnType<typeof setTimeout> | undefined;

export default function Tickets() {
  const { isStaff, user, isIt } = useAuth();
  const isAssignedIT = false; // Restriction removed so IT can forward tickets
  const qc = useQueryClient();
  const [search, setSearch] = useSearchParams();
  const [view, setView] = useState<"list" | "kanban">("list");

  // ── New Ticket Modal state ──────────────────────────────────────
  const [isNewOpen, setIsNewOpen] = useState(false);

  // Auto-open modal when navigated with ?new=true (e.g. from header button)
  useEffect(() => {
    if (search.get("new") === "true") {
      setIsNewOpen(true);
      const next = new URLSearchParams(search);
      next.delete("new");
      setSearch(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newPriority, setNewPriority] = useState<Priority>("MEDIUM");
  const [newCategory, setNewCategory] = useState<Category | "">("");
  const [newIssue, setNewIssue] = useState("");
  const [customIssue, setCustomIssue] = useState("");
  const [newAssigneeId, setNewAssigneeId] = useState("");

  interface Screenshot {
    id: string;
    file: File;
    previewUrl: string;
    name: string;
    size: number;
  }
  const [screenshots, setScreenshots] = useState<Screenshot[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addFiles = (files: File[]) => {
    const validFiles = files.filter((file) => file.type.startsWith("image/"));
    if (validFiles.length !== files.length) {
      toast.error("Only image files are allowed as screenshots.");
    }
    const newScreenshots: Screenshot[] = validFiles.map((file) => ({
      id: Math.random().toString(36).substring(7),
      file,
      previewUrl: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
    }));
    setScreenshots((prev) => [...prev, ...newScreenshots]);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    addFiles(files);
  };

  const removeScreenshot = (id: string) => {
    setScreenshots((prev) => {
      const target = prev.find((s) => s.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((s) => s.id !== id);
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(Array.from(e.dataTransfer.files || []));
  };

  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      if (!isNewOpen) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      const files: File[] = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item && item.type.indexOf("image") !== -1) {
          const file = item.getAsFile();
          if (file) {
            const pastedFile = new File(
              [file],
              `screenshot_${new Date().toISOString().replace(/[:.]/g, "-")}.png`,
              { type: file.type },
            );
            files.push(pastedFile);
          }
        }
      }
      if (files.length > 0) {
        addFiles(files);
        toast.success(`Pasted ${files.length} screenshot(s) from clipboard!`);
      }
    };
    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewOpen]);

  function resetNewForm() {
    setNewTitle("");
    setNewDesc("");
    setNewPriority("MEDIUM");
    setNewCategory("");
    setNewIssue("");
    setCustomIssue("");
    setNewAssigneeId("");
    setScreenshots((prev) => {
      prev.forEach((s) => URL.revokeObjectURL(s.previewUrl));
      return [];
    });
  }

  const createMut = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const ticket: TicketType = (await api.post("/api/tickets", data)).data.ticket;
      if (screenshots.length > 0) {
        for (const s of screenshots) {
          try {
            const form = new FormData();
            form.append("file", s.file);
            await api.post(`/api/tickets/${ticket.id}/attachments`, form, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          } catch (err) {
            console.error(`Failed to upload screenshot: ${s.name}`, err);
            toast.error(`Failed to upload ${s.name}: ${apiError(err)}`);
          }
        }
      }
      return ticket;
    },
    onSuccess: () => {
      toast.success("Ticket created successfully");
      setIsNewOpen(false);
      resetNewForm();
      qc.invalidateQueries({ queryKey: ["tickets.list"] });
      qc.invalidateQueries({ queryKey: ["dashboard.recent"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const handleNewSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim() || !newDesc.trim()) {
      toast.error("Please fill out title and description");
      return;
    }
    if (!newCategory) {
      toast.error("Please select a category");
      return;
    }
    const finalIssue = newIssue === "Others" ? customIssue.trim() : newIssue;

    const payload: any = {
      title: newTitle.trim(),
      description: newDesc.trim(),
      priority: newPriority,
      category: newCategory,
      issue: finalIssue || undefined,
      assigneeId: newAssigneeId || undefined,
    };
    if (!payload.assigneeId) {
      delete payload.assigneeId;
    }
    createMut.mutate(payload);
  };

  // ── Filters / list state ────────────────────────────────────────
  // Read filters from URL
  const q = search.get("q") || "";
  const statuses = search.getAll("status");
  const priorities = search.getAll("priority");
  const categories = search.getAll("category");
  const slaState = search.get("slaState") || "";
  const mine = search.get("mine") === "true";
  const assigneeId = search.get("assigneeId") || "";
  const page = parseInt(search.get("page") || "1", 10);

  const updateParam = (
    key: string,
    value: string | string[] | boolean | null | undefined,
    multi = false,
  ) => {
    const next = new URLSearchParams(search);
    if (multi) {
      next.delete(key);
      (Array.isArray(value) ? value : [value as string])
        .filter(Boolean)
        .forEach((v) => next.append(key, v as string));
    } else if (
      value === "" ||
      value === null ||
      value === undefined ||
      value === false
    ) {
      next.delete(key);
    } else {
      next.set(key, value as string);
    }
    if (key !== "page") next.delete("page");
    setSearch(next, { replace: true });
  };

  const clearFilters = () => setSearch(new URLSearchParams(), { replace: true });

  const params = useMemo(() => {
    const p: Record<string, unknown> = { page, pageSize: 20 };
    if (q) p.q = q;
    if (statuses.length) p.status = statuses;
    if (priorities.length) p.priority = priorities;
    if (categories.length) p.category = categories;
    if (slaState) p.slaState = slaState;
    if (mine) p.mine = true;
    if (isAssignedIT) {
      p.assigneeId = user?.id;
    } else if (assigneeId) {
      p.assigneeId = assigneeId;
    }
    return p;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, statuses, priorities, categories, slaState, mine, assigneeId, page, isAssignedIT, user?.id]);

  const { data, isLoading } = useQuery({
    queryKey: ["tickets.list", params],
    queryFn: async () =>
      (await api.get<Paginated<TicketType>>("/api/tickets", { params })).data,
    placeholderData: keepPreviousData,
    refetchInterval: 10_000, // Poll every 10s so new tickets from other users appear
  });

  const { data: assigneesData } = useQuery({
    queryKey: ["users.assignees"],
    queryFn: async () => {
      const items = (await api.get<{ items: UserSummary[] }>("/api/users/assignees")).data.items;
      return items.filter((u) => u.role === "IT" || u.role === "ADMIN");
    },
  });

  // The Assignee filter only lists IT/ADMIN agents who also hold one of the IT
  // departments (Software, Hardware, System, Network, Access). Matches the
  // primary department or any additional department, case-insensitively.
  const itDepartments = useItDepartments();
  const filterAssignees = useMemo(() => {
    const allowed = new Set(itDepartments.map((d) => d.toLowerCase()));
    return (assigneesData || []).filter((a) =>
      [a.department, ...(a.additionalDepartments || [])]
        .filter(Boolean)
        .some((d) => allowed.has((d as string).toLowerCase())),
    );
  }, [assigneesData, itDepartments]);

  const items = data?.items || [];
  const total = data?.total || 0;
  const totalPages = data?.totalPages || 1;

  const hasActiveFilters =
    q ||
    statuses.length ||
    priorities.length ||
    categories.length ||
    slaState ||
    mine ||
    assigneeId;

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="page-title">Tickets</h1>
          <p className="page-subtitle">
            {total.toLocaleString()} total {hasActiveFilters && "· filtered"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="glass-subtle rounded-xl p-0.5 flex">
            <button
              onClick={() => setView("list")}
              className={cn(
                "px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5",
                view === "list" ? "bg-white/10 text-white" : "text-slate-300",
              )}
              title="List view"
            >
              <List className="w-3.5 h-3.5" /> List
            </button>
            <button
              onClick={() => setView("kanban")}
              className={cn(
                "px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5",
                view === "kanban" ? "bg-white/10 text-white" : "text-slate-300",
              )}
              title="Kanban view"
            >
              <LayoutGrid className="w-3.5 h-3.5" /> Kanban
            </button>
          </div>
          <button onClick={() => setIsNewOpen(true)} className="btn-primary shadow-glow">
            <Plus className="w-4 h-4" /> New Ticket
          </button>
        </div>
      </div>

      {/* Filters */}
      <GlassCard className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search title or description…"
              defaultValue={q}
              onChange={(e) => {
                const v = e.target.value;
                clearTimeout(searchDebounce);
                searchDebounce = setTimeout(() => updateParam("q", v), 350);
              }}
              className="input pl-10"
            />
          </div>
          {isStaff && (
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-slate-200">
              <input
                type="checkbox"
                checked={mine}
                onChange={(e) =>
                  updateParam("mine", e.target.checked ? "true" : false)
                }
              />
              My submitted only
            </label>
          )}
          {hasActiveFilters && (
            <button onClick={clearFilters} className="btn-ghost">
              <X className="w-4 h-4" /> Clear
            </button>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <MultiPill
            label="Status"
            value={statuses}
            options={STATUSES.map((s) => ({ value: s, label: STATUS_META[s].label }))}
            onChange={(v) => updateParam("status", v, true)}
          />
          <MultiPill
            label="Priority"
            value={priorities}
            options={PRIORITIES.map((p) => ({ value: p, label: p }))}
            onChange={(v) => updateParam("priority", v, true)}
          />
          <MultiPill
            label="Category"
            value={categories}
            options={CATEGORIES.map((c) => ({ value: c, label: c }))}
            onChange={(v) => updateParam("category", v, true)}
          />
          {!isAssignedIT && (
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider text-slate-400 mr-1">
                Assignee
              </span>
              {[
                { v: "", l: "Any" },
                { v: "unassigned", l: "Unassigned" },
                ...filterAssignees.map((a) => ({ v: a.id, l: a.name })),
              ].map((a) => {
                const active = assigneeId === a.v;
                return (
                  <button
                    key={a.l}
                    onClick={() => updateParam("assigneeId", a.v)}
                    className={cn(
                      "px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all",
                      active
                        ? "bg-brand-500/25 border-brand-400/50 text-white"
                        : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10",
                    )}
                  >
                    {a.l}
                  </button>
                );
              })}
            </div>
          )}
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider text-slate-400 mr-1">
              SLA
            </span>
            {[
              { v: "", l: "Any" },
              { v: "ok", l: "On track" },
              { v: "at-risk", l: "At risk" },
              { v: "breached", l: "Breached" },
            ].map((s) => {
              const active = slaState === s.v;
              return (
                <button
                  key={s.l}
                  onClick={() => updateParam("slaState", s.v)}
                  className={cn(
                    "px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all",
                    active
                      ? "bg-amber-500/20 border-amber-400/40 text-white"
                      : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10",
                  )}
                >
                  {s.l}
                </button>
              );
            })}
          </div>
        </div>
      </GlassCard>

      {/* Results */}
      {isLoading ? (
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="skeleton h-16" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <GlassCard>
          <EmptyState
            icon={Filter}
            title="No tickets match"
            description="Try clearing filters or creating a new ticket."
            action={
              <button onClick={() => setIsNewOpen(true)} className="btn-primary inline-flex">
                <Plus className="w-4 h-4" /> New Ticket
              </button>
            }
          />
        </GlassCard>
      ) : view === "list" ? (
        <ListView
          items={items}
          onAssigneeClick={(id) => updateParam("assigneeId", id)}
        />
      ) : (
        <KanbanView
          items={items}
          onAssigneeClick={(id) => updateParam("assigneeId", id)}
        />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            disabled={page <= 1}
            onClick={() => updateParam("page", String(page - 1))}
            className="btn-ghost disabled:opacity-40"
          >
            <ChevronLeft className="w-4 h-4" /> Prev
          </button>
          <span className="text-sm text-slate-300 px-3">
            Page <b className="text-white">{page}</b> of {totalPages}
          </span>
          <button
            disabled={page >= totalPages}
            onClick={() => updateParam("page", String(page + 1))}
            className="btn-ghost disabled:opacity-40"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* ── New Ticket Modal ───────────────────────────────────── */}
      <Modal
        open={isNewOpen}
        onClose={() => {
          setIsNewOpen(false);
          resetNewForm();
        }}
        title="New Ticket"
        size="xl"
      >
        <p className="text-sm text-slate-400 -mt-2 mb-5">
          Describe the issue clearly. Agents will adjust priority if needed.
        </p>

        <form onSubmit={handleNewSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6">
            {/* ── Left Column: Title, Description, Screenshots ── */}
            <div className="space-y-5 min-w-0">
              {/* Title */}
              <div>
                <label className="label">Title</label>
                <input
                  className="input"
                  required
                  placeholder="One-line summary, e.g. Cannot connect to VPN"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                />
              </div>

              {/* Priority Info Box */}
              <div className="bg-white/[0.03] border border-white/10 p-3.5 rounded-xl flex items-start gap-3 transition-colors duration-300">
                <div className="mt-0.5 shrink-0">
                  <span className={cn("w-2.5 h-2.5 rounded-full inline-block shadow-sm", PRIORITY_META[newPriority].dot)} />
                </div>
                <div>
                  <div className={cn("text-[11px] font-bold tracking-wider uppercase mb-1", PRIORITY_META[newPriority].text)}>
                    {PRIORITY_META[newPriority].label} Priority
                  </div>
                  <div className="text-[13px] text-slate-300 leading-relaxed">
                    {PRIORITY_META[newPriority].description}
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="label">Description</label>
                <textarea
                  className="textarea"
                  rows={5}
                  required
                  placeholder="What did you try? What is the impact? Any error messages?"
                  value={newDesc}
                  onChange={(e) => setNewDesc(e.target.value)}
                />
              </div>

              {/* Screenshots */}
              <div>
                <label className="label flex items-center justify-between">
                  <span>Screenshots (Optional)</span>
                  {screenshots.length > 0 && (
                    <span className="text-xs text-slate-400 font-medium">
                      {screenshots.length} screenshot{screenshots.length > 1 ? "s" : ""} added
                    </span>
                  )}
                </label>
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={cn(
                    "border border-dashed rounded-2xl p-4 transition-all duration-300 text-center cursor-pointer flex items-center justify-center gap-3 outline-none group",
                    isDragging
                      ? "border-brand-400 bg-brand-500/10 scale-[1.01] shadow-glow"
                      : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/20",
                  )}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/*"
                    multiple
                    onChange={handleFileChange}
                  />
                  <div className="w-9 h-9 rounded-xl bg-brand-500/15 border border-brand-400/30 flex items-center justify-center text-brand-300 shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <UploadCloud className="w-4.5 h-4.5" />
                  </div>
                  <p className="text-sm text-slate-300">
                    Drag & drop,{" "}
                    <span className="text-brand-400 font-semibold hover:underline">browse</span>
                    , or paste{" "}
                    <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-white font-mono text-[10px]">Ctrl+V</kbd>
                  </p>
                </div>

                {screenshots.length > 0 && (
                  <div className="grid grid-cols-3 gap-3 mt-3 max-h-[200px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-white/10 hover:scrollbar-thumb-white/20">
                    {screenshots.map((s) => (
                      <div
                        key={s.id}
                        className="group/ss relative rounded-xl border border-white/10 bg-white/[0.02] p-1.5 overflow-hidden transition-all duration-300 hover:border-white/20"
                      >
                        <div className="aspect-video w-full rounded-lg overflow-hidden bg-slate-950 relative border border-white/5">
                          <img
                            src={s.previewUrl}
                            alt={s.name}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover/ss:scale-105"
                          />
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/ss:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeScreenshot(s.id);
                              }}
                              className="p-1.5 rounded-lg bg-critical-500/80 hover:bg-critical-500 text-white transition-all transform hover:scale-110"
                              title="Remove screenshot"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="mt-1.5 px-1 text-left">
                          <div className="text-[11px] text-white truncate font-medium" title={s.name}>{s.name}</div>
                          <div className="text-[10px] text-slate-400">{(s.size / 1024).toFixed(1)} KB</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* ── Right Column: Priority & Category ── */}
            <div className="w-full md:w-[280px] space-y-6">
              {/* Priority */}
              <div>
                <label className="label">Priority (suggested)</label>
                <div className="grid grid-cols-2 gap-2">
                  {PRIORITIES.map((p) => {
                    const meta = PRIORITY_META[p];
                    const active = newPriority === p;
                    return (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setNewPriority(p)}
                        className={cn(
                          "rounded-xl px-3 py-2.5 text-left text-sm border transition-all duration-300 transform active:scale-95 hover:scale-[1.02]",
                          active
                            ? `${meta.bg} ${meta.border} ring-2 ${meta.ring} text-white font-semibold shadow-glow`
                            : "bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.06] hover:border-white/20",
                        )}
                      >
                        <span className="inline-flex items-center gap-2">
                          <span className={cn("w-2 h-2 rounded-full", meta.dot)} />
                          {meta.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-slate-500 mt-2">
                  An agent may adjust this based on impact.
                </p>

                {PRIORITY_TEMPLATES[newPriority]?.length > 0 && (
                  <div className="mt-4">
                    <label className="label text-brand-300">Issue</label>
                    <select
                      className="select border-white/20 hover:border-white/30"
                      value={newIssue}
                      onChange={(e) => {
                        const val = e.target.value;
                        setNewIssue(val);
                        if (val && ISSUE_CATEGORIES[val]) {
                          setNewCategory(ISSUE_CATEGORIES[val]);
                        }
                      }}
                    >
                      <option value="">Select an issue...</option>
                      {PRIORITY_TEMPLATES[newPriority].map((tpl) => (
                        <option key={tpl} value={tpl}>{tpl}</option>
                      ))}
                      <option value="Others">Others</option>
                    </select>
                    {newIssue === "Others" && (
                      <input
                        type="text"
                        className="input mt-2"
                        placeholder="Type specific issue..."
                        value={customIssue}
                        onChange={(e) => setCustomIssue(e.target.value)}
                        required
                      />
                    )}
                  </div>
                )}
              </div>

              {/* Category */}
              <div>
                <label className="label">Category <span className="text-critical-400">*</span></label>
                <div className="grid grid-cols-2 gap-2">
                  {CATEGORIES.map((c, i) => {
                    const active = newCategory === c;
                    const isRight = i % 2 === 1;
                    return (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setNewCategory(c)}
                        className={cn(
                          "group relative rounded-xl px-3 py-2.5 text-sm border transition-all text-left duration-300 transform active:scale-95 hover:scale-[1.02]",
                          active
                            ? "bg-brand-500/20 border-brand-400/50 text-white ring-2 ring-brand-400/30 shadow-glow font-semibold scale-[1.01]"
                            : "bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.06] hover:border-white/20",
                        )}
                      >
                        {CATEGORY_META[c].label}
                        {CATEGORY_META[c].description && (
                          <div className={cn(
                            "absolute z-50 bottom-full mb-2 w-56 p-2.5 text-xs text-[#e2e8f0] bg-slate-800 border border-white/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-xl drop-shadow-2xl break-words whitespace-normal text-left hidden group-hover:block",
                            isRight ? "right-0" : "left-0"
                          )}>
                            {CATEGORY_META[c].description}
                            <div className={cn(
                              "absolute top-full border-[6px] border-transparent border-t-slate-800",
                              isRight ? "right-6" : "left-6"
                            )}></div>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {newCategory && (
                <div className="mt-4 animate-in fade-in slide-in-from-top-2 duration-300">
                  <label className="label">IT Assigned (Optional)</label>
                  <select
                    className="select border-white/20 hover:border-white/30"
                    value={newAssigneeId}
                    onChange={(e) => setNewAssigneeId(e.target.value)}
                  >
                    <option value="">Choose an Assignee</option>
                    {assigneesData
                      ?.filter(
                        (a) =>
                          (a.department && a.department.toUpperCase() === newCategory) ||
                          (a.additionalDepartments && a.additionalDepartments.some((d: string) => d.toUpperCase() === newCategory))
                      )
                      .map((a) => {
                        const depts = [a.department, ...(a.additionalDepartments || [])].filter(Boolean);
                        return (
                          <option key={a.id} value={a.id}>
                            {a.name} {depts.length > 0 ? `(${depts.join(", ")})` : ""}
                          </option>
                        );
                      })}
                  </select>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Choose a specific agent or leave empty to auto-assign.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-5 mt-5 border-t border-white/5">
            <button
              type="button"
              className="btn-ghost"
              onClick={() => {
                setIsNewOpen(false);
                resetNewForm();
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMut.isPending || !newCategory}
              className={cn(
                "btn-primary transition-all duration-300",
                !newCategory &&
                "opacity-50 cursor-not-allowed bg-slate-800 border-slate-700 text-slate-500 hover:bg-slate-800",
              )}
            >
              {createMut.isPending ? (
                <>
                  <Spinner />
                  {screenshots.length > 0 ? "Uploading..." : "Submitting..."}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Submit Ticket
                </>
              )}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function ListView({
  items,
  onAssigneeClick,
}: {
  items: TicketType[];
  onAssigneeClick: (id: string) => void;
}) {
  return (
    <GlassCard padded={false}>
      {items.length > 0 && (
        <div className="hidden lg:grid grid-cols-[80px_1fr_140px_170px_100px_140px_100px] gap-3 px-4 py-3 bg-white/[0.02] border-b border-white/5 text-[10px] uppercase tracking-wider text-slate-500 font-bold select-none">
          <div className="col-span-1">Ticket ID</div>
          <div className="col-span-1">Ticket Details</div>
          <div className="col-span-1">Submitter</div>
          <div className="col-span-1">Priority & Category</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-1">Assignee</div>
          <div className="col-span-1 text-center">SLA</div>
        </div>
      )}
      <div className="divide-y divide-white/5">
        {items.map((t) => (
          <Link
            key={t.id}
            to={`/tickets/${t.id}`}
            className="grid grid-cols-12 lg:grid-cols-[80px_1fr_140px_170px_100px_140px_100px] gap-3 items-center px-4 py-3.5 hover:bg-white/[0.03] transition-colors"
          >
            <div className="col-span-12 lg:col-span-1 flex items-center">
              <span className="font-mono text-[11px] text-slate-400 bg-white/5 px-2 py-1 rounded border border-white/10">
                {t.id.slice(-8).toUpperCase()}
              </span>
            </div>
            <div className="col-span-12 lg:col-span-1 min-w-0">
              <div className="text-sm text-white font-medium truncate">
                {t.title}
              </div>
              <div className="text-xs text-slate-400 truncate mt-0.5">
                {truncate(t.description, 80)}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mt-1.5 flex items-center gap-1.5">
                {fmtRelative(t.createdAt)} · {t._count?.comments || 0} comments · {t._count?.attachments || 0} files
              </div>
            </div>
            <div className="col-span-12 lg:col-span-1 flex items-center text-xs text-slate-400">
              {t.submitter ? (
                <div className="flex items-center gap-1.5 text-left" title={t.submitter.name}>
                  <Avatar name={t.submitter.name} url={t.submitter.avatarUrl} size="xs" />
                  <span className="truncate max-w-[100px]">{t.submitter.name}</span>
                </div>
              ) : (
                <span className="italic text-slate-500">Unknown</span>
              )}
            </div>
            <div className="col-span-6 lg:col-span-1 flex items-center gap-1.5 flex-wrap">
              <PriorityBadge priority={t.priority} />
              <CategoryBadge category={t.category} />
            </div>
            <div className="col-span-3 lg:col-span-1 flex items-center">
              <StatusBadge status={t.status} />
            </div>
            <div className="col-span-3 lg:col-span-1 flex items-center text-xs text-slate-400">
              {t.assignee ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onAssigneeClick(t.assigneeId as string);
                  }}
                  className="flex items-center gap-1.5 hover:text-white transition-colors text-left"
                  title={`Filter by ${t.assignee.name}`}
                >
                  <Avatar name={t.assignee.name} url={t.assignee.avatarUrl} size="xs" />
                  <span className="truncate">{t.assignee.name}</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onAssigneeClick("unassigned");
                  }}
                  className="italic text-slate-500 hover:text-slate-300 transition-colors"
                  title="Filter by unassigned"
                >
                  Unassigned
                </button>
              )}
            </div>
            <div className="col-span-12 lg:col-span-1 flex lg:justify-center">
              <SLACountdown
                deadline={t.slaDeadline}
                priority={t.priority}
                status={t.status}
                compact
              />
            </div>
          </Link>
        ))}
      </div>
    </GlassCard>
  );
}

function KanbanView({
  items,
  onAssigneeClick,
}: {
  items: TicketType[];
  onAssigneeClick: (id: string) => void;
}) {
  const grouped = STATUSES.reduce(
    (acc, s) => {
      acc[s] = items.filter((t) => t.status === s);
      return acc;
    },
    {} as Record<Status, TicketType[]>,
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
      {STATUSES.map((status) => (
        <GlassCard key={status} padded={false} className="p-3">
          <div className="flex items-center justify-between mb-3 px-1">
            <StatusBadge status={status} />
            <span className="text-xs text-slate-400">{grouped[status].length}</span>
          </div>
          <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
            {grouped[status].length === 0 ? (
              <div className="text-xs text-slate-500 italic px-2 py-4 text-center">
                Empty
              </div>
            ) : (
              grouped[status].map((t) => (
                <Link
                  key={t.id}
                  to={`/tickets/${t.id}`}
                  className="block glass-subtle rounded-xl p-3 hover:bg-white/10 transition-colors"
                >
                  <div className="text-sm text-white font-medium line-clamp-2">
                    {t.title}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-1.5 flex items-center gap-1.5">
                    <span className="opacity-75">From:</span>
                    {t.submitter ? (
                      <span className="text-slate-300 font-medium truncate">{t.submitter.name}</span>
                    ) : (
                      <span className="italic">Unknown</span>
                    )}
                  </div>
                  <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                    <PriorityBadge priority={t.priority} />
                    <CategoryBadge category={t.category} />
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[11px] text-slate-400">
                    {t.assignee ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onAssigneeClick(t.assigneeId as string);
                        }}
                        className="flex items-center gap-1.5 hover:text-white transition-colors text-left"
                        title={`Filter by ${t.assignee.name}`}
                      >
                        <Avatar name={t.assignee.name} url={t.assignee.avatarUrl} size="xs" />
                        <span className="truncate max-w-[100px]">
                          {t.assignee.name}
                        </span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onAssigneeClick("unassigned");
                        }}
                        className="italic text-slate-500 hover:text-slate-300 transition-colors"
                        title="Filter by unassigned"
                      >
                        Unassigned
                      </button>
                    )}
                    <SLACountdown
                      deadline={t.slaDeadline}
                      priority={t.priority}
                      status={t.status}
                      compact
                    />
                  </div>
                </Link>
              ))
            )}
          </div>
        </GlassCard>
      ))}
    </div>
  );
}

